import React, { useState, useEffect } from "react";
import { Button, TextField, Autocomplete, Avatar } from "@mui/material";
import "react-datepicker/dist/react-datepicker.css";
import "react-toastify/dist/ReactToastify.css";
import CloseIcon from "@mui/icons-material/Close";
import DatePickerWithTimeComponent from "../DatePickerWithTimeComponent";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";
import { showCustomToast } from "../Misc/CustomToast";
import SearchIcon from "@mui/icons-material/Search";
import { query, where, limit, onSnapshot } from "firebase/firestore";

import Chip from "@mui/material/Chip";

function AddQuickNoteWithClients(props) {

  const { currentUser } = useAuth();

  // useEffect(() => {
  //   recordMetrics("Clients", "quickNotes", currentUser.email, "write", "bodhi");
  // }, []);

  const [quickNoteText, setQuickNoteText] = useState("");
  const [effectiveDateTime, setEffectiveDateTime] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const draftKey = `draft:quickNoteWithClients:${currentUser?.uid || "anon"}`;
  const draftDelay = 800;
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const handleDateTimeChange = (event) => {
    setEffectiveDateTime(event);
  };

  const handleAddQuickNote = async (note, clientID, effectiveDateTime) => {
    if (!selectedClient) {
      showCustomToast(`Please select a client.`, "error");
      return;
    }

    const noteData = {
      noteType: "quickNote",
      noteContent: note,
      clientId: selectedClient.id,
      createdAt: serverTimestamp(),
      effectiveDateTime: effectiveDateTime,
      doneBy: currentUser.displayName,
      clientFirstName: selectedClient.firstName,
      clientLastName: selectedClient.lastName,
      clientProfilePicture: selectedClient.profilePicture || "",
    };

    if (quickNoteText && effectiveDateTime) {
      setIsLoading(true);
      try {
        await addDoc(collection(db, "notes"), noteData);
        showCustomToast(`Quick note successfully added!`, "success");

        setIsLoading(false);
        try {
          localStorage.removeItem(draftKey);
        } catch {}
        props.closemodal();
      } catch (error) {
        console.log("error adding note :", error);
        showCustomToast(`Problem when adding note`, "error");
      }
    } else {
      showCustomToast(
        `Some mandatory fields are empty. Please check.`,
        "error"
      );
    }
  };

  const handleSubmit = () => {
    handleAddQuickNote(quickNoteText, props.clientId, effectiveDateTime);
  };

  const handleChange = (event) => {
    setQuickNoteText(event.target.value);
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [clients, setClients] = useState([]);
  const [, setLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedClientName, setSelectedClientName] = useState("");
  const [unsubscribe, setUnsubscribe] = useState(null);

  // Restore draft on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.text) setQuickNoteText(parsed.text);
        if (parsed?.date) {
          const d = new Date(parsed.date);
          if (!isNaN(d)) setEffectiveDateTime(d);
        }
        if (parsed?.selectedClient) {
          setSelectedClient(parsed.selectedClient);
          const name =
            parsed.selectedClientName ||
            `${parsed.selectedClient.firstName || ""} ${
              parsed.selectedClient.lastName || ""
            }`.trim();
          setSelectedClientName(name);
          setSearchQuery(name);
        }
      }
    } catch (e) {
      console.warn("QuickNoteWithClients draft load error:", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Autosave draft with debounce
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        localStorage.setItem(
          draftKey,
          JSON.stringify({
            text: quickNoteText,
            date: effectiveDateTime?.toISOString?.() || null,
            selectedClient: selectedClient
              ? {
                  id: selectedClient.id,
                  firstName: selectedClient.firstName,
                  lastName: selectedClient.lastName,
                  profilePicture: selectedClient.profilePicture || "",
                }
              : null,
            selectedClientName,
          })
        );
        setIsSavingDraft(false);
      } catch (e) {
        console.warn("QuickNoteWithClients draft save error:", e);
      }
    }, draftDelay);
    setIsSavingDraft(true);
    return () => clearTimeout(t);
  }, [
    quickNoteText,
    effectiveDateTime,
    selectedClient,
    selectedClientName,
    draftKey,
  ]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // 500ms de délai

    // Nettoyer le timeout lors de la mise à jour de searchQuery
    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  useEffect(() => {
    let newUnsubscribe;

    if (debouncedSearchQuery.length > 0) {
      setLoading(true);

      const search = query(
        collection(db, "clients"),
        where(
          "searchTerms",
          "array-contains-any",
          debouncedSearchQuery.trim().toLowerCase().split(" ")
        ),
        limit(10)
      );

      newUnsubscribe = onSnapshot(search, (snapshot) => {
        const results = [];
        snapshot.forEach((doc) => {
          results.push({
            id: doc.id,
            ...doc.data(),
          });
        });
        setClients(results);
        setLoading(false);
      });
    } else {
      setClients([]);
    }

    setUnsubscribe(() => newUnsubscribe);
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [debouncedSearchQuery]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setSelectedClient(null);
    setSelectedClientName("");
  };

  const handleSelect = (event, value) => {
    if (value) {
      const client = clients.find(
        (client) => `${client.firstName} ${client.lastName}` === value
      );
      if (client) {
        setSelectedClient(client);
        setSelectedClientName(`${client.firstName} ${client.lastName}`);
      } else {
        setSelectedClient(null);
        setSelectedClientName("");
      }
    } else {
      setSelectedClient(null);
      setSelectedClientName("");
    }
  };

  return (
    <>
      <div style={{ width: "auto", height: "100%" }}>
        <div className="line1-modal-percent">
          <div className="modal-title">Add Quick Note</div>
          <CloseIcon
            style={{
              marginRight: "16px",
              color: "rgb(214, 213, 213)",
              cursor: "pointer",
            }}
            onClick={props.closemodal}
          />
        </div>

        <div
          style={{
            width: "100%",
            marginTop: "10px",
            height: "75%",
            overflowY: "scroll",
            overflowX: "hidden",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div
              style={{
                width: "48%",
                paddingLeft: "0px",
                paddingTop: "10px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "end",
              }}
            >
              <div
                style={{
                  width: "100%",
                  paddingLeft: "25px",
                }}
              >
                <Autocomplete
                  freeSolo
                  disableClearable
                  options={clients.map(
                    (client) => `${client.firstName} ${client.lastName}`
                  )}
                  renderOption={(props, option, { selected }) => {
                    const client = clients.find(
                      (client) =>
                        `${client.firstName} ${client.lastName}` === option
                    );

                    return (
                      <li
                        {...props}
                        key={client.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          borderBottom: "1px solid #BEBCBC",
                        }}
                      >
                        {client.profilePicture && (
                          <Avatar
                            src={client.profilePicture}
                            style={{
                              marginRight: "10px",
                              width: "50px",
                              height: "50px",
                            }}
                          />
                        )}
                        <div style={{ width: "220px" }}>
                          {`${client.firstName} ${client.lastName}`}
                        </div>
                        {client.clientIdentifier}
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <div
                      ref={params.InputProps.ref}
                      style={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <input
                        type="text"
                        {...params.inputProps}
                        onChange={handleSearchChange}
                        value={searchQuery}
                        placeholder="Search client…"
                        style={{
                          width: "100%",
                          height: "45px",
                          borderRadius: "5px",
                          paddingLeft: "10px",
                          background: "#FFFFFF",
                          border: "1px solid #BEBCBC",
                        }}
                      />
                      <SearchIcon />
                    </div>
                  )}
                  onChange={handleSelect}
                />
              </div>

              <div
                style={{
                  marginTop: "10px",
                  fontWeight: "bold",
                  display: "flex",
                  justifyContent: "flex-start",
                  width: "100%",
                  paddingLeft: "25px",
                }}
              >
                {selectedClientName ? (
                  <>Selected Client: {selectedClientName}</>
                ) : (
                  <>Please select a client</>
                )}
              </div>
            </div>
            <div
              style={{
                width: "48%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <DatePickerWithTimeComponent
                title="Effective date and time"
                value={effectiveDateTime}
                onChange={handleDateTimeChange}
                leftMargin="none"
              />
            </div>
          </div>

          <div className="line2-column-modal-v2">
            <div className="label-textfield2">Note content</div>
            <TextField
              name="quickNote"
              value={quickNoteText}
              onChange={handleChange}
              style={{
                width: "95%",
                borderRadius: "5px",
                backgroundColor: "#f2eeef",
              }}
              placeholder="Type quick session note here"
              multiline
              rows={12}
            />
            {/* <div
              className="line3-modal-nopad"
              style={{
                paddingTop: "10px",
                marginBottom: "20px",
              }}
            >
              <DatePickerWithTimeComponent
                title="Effective date and time"
                value={effectiveDateTime}
                onChange={handleDateTimeChange}
                leftMargin="none"
              />
            </div> */}
          </div>
        </div>
        <div
          className="line4-modal-percent"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 10,
          }}
        >
          <Chip
            label={
              isSavingDraft
                ? "Saving draft..."
                : quickNoteText || selectedClient
                ? "Draft saved"
                : "No draft"
            }
            size="small"
            sx={{
              backgroundColor: isSavingDraft
                ? "#f9a825"
                : quickNoteText || selectedClient
                ? "#2e7d32"
                : "#607d8b",
              color: "#fff",
              fontWeight: 700,
            }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              try {
                localStorage.removeItem(draftKey);
              } catch {}
              setQuickNoteText("");
              setEffectiveDateTime(new Date());
              setSelectedClient(null);
              setSelectedClientName("");
              setSearchQuery("");
            }}
          >
            Clear draft
          </Button>
          <Button
            variant="contained"
            style={{ backgroundColor: "white", color: "black" }}
            onClick={props.closemodal}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            style={
              isLoading
                ? {
                    backgroundColor: "red",
                    color: "white",
                    marginLeft: "15px",
                  }
                : {
                    backgroundColor: "#1565C0",
                    color: "white",
                    marginLeft: "15px",
                  }
            }
            disabled={isLoading}
            onClick={handleSubmit}
          >
            {isLoading ? "ADDING NOTES..." : "ADD NOTES"}
          </Button>
        </div>
      </div>
    </>
  );
}

export default AddQuickNoteWithClients;
